import React, { useState, useMemo } from "react";
import { ChevronDown, ChevronRight, Menu, X, Search, Pin, PinOff } from "lucide-react";
import {
  ADMIN_TAB_GROUPS,
  ADMIN_STANDALONE_TAB,
  canSeeAdminTab,
} from "./adminTabsConfig";

const PIN_KEY = "doozygo_admin_pinned";

const loadPinned = () => {
  try { return JSON.parse(localStorage.getItem(PIN_KEY) || "[]"); } catch { return []; }
};

const AdminSidebar = ({ activeTab, setActiveTab, allowedTabs, auth, newBookingCount = 0, collapsed, setCollapsed }) => {
  const [query, setQuery] = useState("");
  const [pinned, setPinned] = useState(loadPinned);
  const [openGroups, setOpenGroups] = useState(() => {
    const initial = {};
    const isSuperAdmin = auth?.role === "admin" || auth?.role === "super_admin";
    ADMIN_TAB_GROUPS.forEach((g, i) => {
      if (g.items.some(item => item.id === activeTab)) initial[i] = true;
      if (isSuperAdmin) initial[i] = true;
    });
    if (Object.keys(initial).length === 0) initial[0] = true;
    return initial;
  });

  const toggleGroup = (idx) => setOpenGroups(prev => ({ ...prev, [idx]: !prev[idx] }));
  const canSee = (tabId) => canSeeAdminTab(tabId, { allowedTabs, auth });
  const StandaloneIcon = ADMIN_STANDALONE_TAB.icon;

  const togglePin = (e, tabId) => {
    e.stopPropagation();
    setPinned(prev => {
      const next = prev.includes(tabId) ? prev.filter(x => x !== tabId) : [...prev, tabId].slice(0, 6);
      try { localStorage.setItem(PIN_KEY, JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  };

  // Flat index of every visible item for search + pinned lookups
  const allItems = useMemo(() => {
    const list = [];
    if (canSee(ADMIN_STANDALONE_TAB.id)) list.push({ ...ADMIN_STANDALONE_TAB });
    ADMIN_TAB_GROUPS.forEach(g => g.items.forEach(it => { if (canSee(it.id)) list.push(it); }));
    return list;
  }, [allowedTabs, auth]); // eslint-disable-line react-hooks/exhaustive-deps

  const q = query.trim().toLowerCase();
  const searchResults = q
    ? allItems.filter(it => it.label.toLowerCase().includes(q))
    : null;

  const pinnedItems = pinned
    .map(id => allItems.find(it => it.id === id))
    .filter(Boolean);

  const testIdFor = (id) => `tab-${id === "sales_dashboard" ? "sales-exec" : id}`;

  // Shared row renderer
  const NavRow = ({ item, indent = true, idPrefix = "" }) => {
    const Icon = item.icon;
    const isActive = activeTab === item.id;
    const showBadge = newBookingCount > 0 && (item.id === "calendar" || item.id === "crm");
    const isPinned = pinned.includes(item.id);
    return (
      <div className="group/navrow relative">
        <button
          onClick={() => setActiveTab(item.id)}
          title={collapsed ? item.label : undefined}
          className={`relative w-full flex items-center ${collapsed ? "justify-center" : ""} gap-2.5 ${indent && !collapsed ? "pl-4 pr-2" : "px-3"} py-2 rounded-lg text-sm transition-colors duration-200 ${
            isActive
              ? "bg-red-50 text-[#DC283C] font-semibold"
              : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
          }`}
          data-testid={`${idPrefix}${testIdFor(item.id)}`}
        >
          {isActive && !collapsed && (
            <span className="absolute left-0 top-1/2 -translate-y-1/2 h-5 w-[3px] rounded-full bg-[#DC283C]" />
          )}
          <Icon size={16} className={isActive ? "text-[#DC283C]" : ""} />
          {!collapsed && <span className="truncate">{item.label}</span>}
          {showBadge && (
            <span className={`${collapsed ? "absolute -top-0.5 -right-0.5" : "ml-auto"}`} data-testid={`new-booking-badge-${item.id}`}>
              <span className="inline-flex items-center justify-center h-4 min-w-4 px-1 rounded-full bg-[#DC283C] text-white text-[9px] font-bold">
                {newBookingCount > 9 ? "9+" : newBookingCount}
              </span>
            </span>
          )}
        </button>
        {!collapsed && (
          <button
            onClick={(e) => togglePin(e, item.id)}
            title={isPinned ? "Unpin" : "Pin to top"}
            className={`absolute right-1.5 top-1/2 -translate-y-1/2 p-1 rounded-md transition-opacity duration-200 ${showBadge ? "hidden" : ""} ${
              isPinned ? "opacity-100 text-[#DC283C]" : "opacity-0 group-hover/navrow:opacity-100 text-slate-400 hover:text-slate-700"
            }`}
            data-testid={`${idPrefix}pin-${item.id}`}
          >
            {isPinned ? <Pin size={12} fill="currentColor" /> : <Pin size={12} />}
          </button>
        )}
      </div>
    );
  };

  return (
    <>
      {!collapsed && (
        <div className="fixed inset-0 bg-black/40 z-40 lg:hidden" onClick={() => setCollapsed(true)} />
      )}

      <aside className={`fixed top-0 left-0 h-full z-50 bg-white border-r border-slate-200 transition-all duration-300 flex flex-col ${
        collapsed ? "-translate-x-full lg:translate-x-0 lg:w-16" : "w-64 translate-x-0"
      }`} data-testid="admin-sidebar">

        {/* Brand / collapse */}
        <div className={`flex items-center ${collapsed ? "justify-center" : "justify-between"} px-3 py-4 border-b border-slate-100`}>
          {!collapsed && (
            <div className="flex items-center gap-2.5 pl-1">
              <div className="admin-logo-box bg-white rounded-lg px-2 py-1 flex items-center justify-center ring-1 ring-slate-100 shadow-sm">
                <img src="/doozygo-logo.png" alt="DoozyGo®" className="h-6 w-auto" data-testid="admin-sidebar-logo" />
              </div>
              <span className="text-slate-900 font-bold text-sm">Admin</span>
            </div>
          )}
          <button onClick={() => setCollapsed(!collapsed)} className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors" data-testid="sidebar-toggle">
            {collapsed ? <Menu size={18} className="text-slate-500" /> : <X size={16} className="text-slate-400" />}
          </button>
        </div>

        {/* User chip */}
        {!collapsed && (
          <div className="mx-3 mt-3 mb-3 px-3 py-2.5 rounded-xl bg-gradient-to-br from-red-50 to-amber-50 border border-red-100/70">
            <p className="text-slate-900 text-sm font-semibold truncate leading-tight">{auth.name || auth.username}</p>
            <p className="text-[#DC283C] text-xs font-semibold mt-0.5">{auth.role_label || auth.role}</p>
          </div>
        )}

        {/* Search */}
        {!collapsed && (
          <div className="px-3 pb-2">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search menu…"
                className="w-full pl-8 pr-7 py-2 rounded-lg bg-slate-100 border border-slate-200 text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#DC283C]/40 focus:bg-white transition-colors"
                data-testid="sidebar-search"
              />
              {query && (
                <button onClick={() => setQuery("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700" data-testid="sidebar-search-clear">
                  <X size={13} />
                </button>
              )}
            </div>
          </div>
        )}

        {/* Navigation */}
        <nav className="admin-sidebar-scroll flex-1 overflow-y-auto px-3 py-1 space-y-1" data-testid="sidebar-nav">

          {/* Search results (flat) */}
          {searchResults ? (
            <div className="space-y-0.5">
              {searchResults.length === 0 && (
                <p className="text-slate-400 text-xs text-center py-6">No menu item matches “{query}”.</p>
              )}
              {searchResults.map(item => <NavRow key={item.id} item={item} indent={false} />)}
            </div>
          ) : (
            <>
              {/* DoozyBot hero */}
              {canSee(ADMIN_STANDALONE_TAB.id) && (
                <button
                  onClick={() => setActiveTab(ADMIN_STANDALONE_TAB.id)}
                  title={collapsed ? ADMIN_STANDALONE_TAB.label : undefined}
                  className={`w-full flex items-center ${collapsed ? "justify-center" : ""} gap-3 px-3 py-2.5 mb-1 rounded-xl text-sm font-semibold transition-colors duration-200 ${
                    activeTab === ADMIN_STANDALONE_TAB.id
                      ? "bg-gradient-to-r from-[#DC283C] to-[#F0637A] text-white shadow-lg shadow-[#DC283C]/25"
                      : "text-slate-900 bg-red-50/70 ring-1 ring-[#DC283C]/20 hover:bg-red-50"
                  }`}
                  data-testid={`tab-${ADMIN_STANDALONE_TAB.id}`}
                >
                  <StandaloneIcon size={16} className={activeTab === ADMIN_STANDALONE_TAB.id ? "text-white" : "text-[#DC283C]"} />
                  {!collapsed && <span>{ADMIN_STANDALONE_TAB.label}</span>}
                  {!collapsed && <span className="ml-auto text-[10px] font-bold uppercase tracking-wider text-slate-400">AI</span>}
                </button>
              )}

              {/* Pinned */}
              {!collapsed && pinnedItems.length > 0 && (
                <div className="pt-1">
                  <p className="px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-slate-400 flex items-center gap-1.5">
                    <Pin size={11} /> Pinned
                  </p>
                  <div className="space-y-0.5">
                    {pinnedItems.map(item => <NavRow key={`pin-${item.id}`} item={item} indent={false} idPrefix="pinned-" />)}
                  </div>
                  <div className="mx-2 my-2 border-t border-slate-200" />
                </div>
              )}

              {/* Groups */}
              {ADMIN_TAB_GROUPS.map((group, gIdx) => {
                const visibleItems = group.items.filter(item => canSee(item.id));
                if (visibleItems.length === 0) return null;
                const isOpen = openGroups[gIdx];
                const hasActive = visibleItems.some(item => item.id === activeTab);
                const GroupIcon = group.icon;

                return (
                  <div key={group.label || gIdx}>
                    {!collapsed && (
                      <button
                        onClick={() => toggleGroup(gIdx)}
                        className={`w-full flex items-center gap-2 px-3 py-2 mt-1 rounded-lg text-[11px] font-bold uppercase tracking-[0.14em] transition-colors duration-200 ${
                          hasActive ? "text-slate-900" : "text-slate-500 hover:text-slate-800"
                        }`}
                        data-testid={`sidebar-group-${gIdx}`}
                      >
                        <GroupIcon size={13} className={group.color} />
                        <span>{group.label}</span>
                        <span className="ml-auto text-slate-400">
                          {isOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                        </span>
                      </button>
                    )}

                    {collapsed && gIdx > 0 && <div className="mx-2 my-1.5 border-t border-slate-200" />}

                    {(isOpen || collapsed) && (
                      <div className="space-y-0.5">
                        {visibleItems.map(item => <NavRow key={item.id} item={item} />)}
                      </div>
                    )}
                  </div>
                );
              })}
            </>
          )}
        </nav>

        {/* Footer */}
        {!collapsed && (
          <div className="px-4 py-3 border-t border-slate-200">
            <p className="text-slate-400 text-[11px] text-center">DoozyGo Admin v3.0</p>
          </div>
        )}
      </aside>
    </>
  );
};

export default AdminSidebar;
